//frontend/src/components/SuperAdmin/SuperAdminManageAdmins.jsx

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { FaUserPlus, FaUserShield, FaEye, FaLock, FaUnlock, FaTrash, FaSearch, FaEdit } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const SuperAdminManageAdmins = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        password: '',
        role: 'admin',
    });
    const [editFormData, setEditFormData] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'admin',
        is_active: true,
    });

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = async () => {
        try {
            const response = await api.get('/admin/users');
            const allUsers = response.data.users.data || [];
            const adminUsers = allUsers.filter(
                user => user.role === 'admin' || user.role === 'superadmin'
            );
            setAdmins(adminUsers);
        } catch (error) {
            console.error('Error loading admins:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post('/users/register/', {
                ...formData,
                confirm_password: formData.password,
            });
            toast.success('✅ Administrateur créé avec succès');
            setShowModal(false);
            loadAdmins();
            setFormData({
                username: '',
                email: '',
                first_name: '',
                last_name: '',
                phone: '',
                password: '',
                role: 'admin',
            });
        } catch (error) {
            toast.error('❌ Erreur lors de la création');
            console.error('Error creating admin:', error);
        }
    };

    const toggleUserStatus = async (userId) => {
        try {
            await api.post(`/users/${userId}/toggle/`);
            toast.success('Statut modifié');
            loadAdmins();
        } catch (error) {
            toast.error('Erreur lors de la modification');
        }
    };

    const handleShowDetails = (admin) => {
        setSelectedAdmin(admin);
        setShowDetailModal(true);
    };

    const handleShowDeleteConfirm = (admin) => {
        setSelectedAdmin(admin);
        setShowDeleteModal(true);
    };

    const handleDeleteAdmin = async () => {
        try {
            await api.delete(`/users/${selectedAdmin.id}/delete/`);
            toast.success('Administrateur supprimé avec succès');
            setShowDeleteModal(false);
            loadAdmins();
        } catch (error) {
            console.error('Error deleting admin:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleShowEdit = (admin) => {
        setSelectedAdmin(admin);
        setEditFormData({
            username: admin.username,
            email: admin.email,
            first_name: admin.first_name,
            last_name: admin.last_name,
            phone: admin.phone,
            role: admin.role,
            is_active: admin.is_active,
        });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        setEditFormData({
            ...editFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/users/${selectedAdmin.id}/update-admin/`, editFormData);
            toast.success('✅ Administrateur modifié avec succès');
            setShowEditModal(false);
            loadAdmins();
        } catch (error) {
            console.error('Error updating admin:', error);
            toast.error('❌ Erreur lors de la modification');
        }
    };

    const filteredAdmins = admins.filter(admin =>
        admin.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${admin.first_name} ${admin.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaUserShield className="me-3" />
                        Gestion des Administrateurs
                    </h2>
                    <p className="text-muted">Gérez les comptes administrateurs</p>
                </Col>
                <Col className="text-end">
                    <Button variant="primary" onClick={() => setShowModal(true)}>
                        <FaUserPlus className="me-2" />
                        Ajouter un admin
                    </Button>
                </Col>
            </Row>

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>Liste des administrateurs</h5>
                    <Badge bg="primary">{filteredAdmins.length} admins</Badge>
                </Card.Header>
                <Card.Body>
                    {/* Barre de recherche */}
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <FaSearch />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        placeholder="Rechercher un administrateur..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary"></div>
                            <p className="mt-3">Chargement...</p>
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Nom complet</th>
                                    <th>Email</th>
                                    <th>Téléphone</th>
                                    <th>Rôle</th>
                                    <th>Statut</th>
                                    <th>Date création</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAdmins.map((admin) => (
                                    <tr key={admin.id}>
                                        <td>{admin.id}</td>
                                        <td>{admin.first_name} {admin.last_name}</td>
                                        <td>{admin.email}</td>
                                        <td>{admin.phone || '-'}</td>
                                        <td>
                                            <Badge bg={admin.role === 'superadmin' ? 'danger' : 'warning'}>
                                                {admin.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={admin.is_active ? 'success' : 'secondary'}>
                                                {admin.is_active ? 'Actif' : 'Inactif'}
                                            </Badge>
                                        </td>
                                        <td>{new Date(admin.created_at).toLocaleDateString()}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline-info"
                                                    onClick={() => handleShowEdit(admin)}
                                                    title="Modifier"
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => handleShowDetails(admin)}
                                                    title="Voir les détails"
                                                >
                                                    <FaEye />
                                                </Button>
                                                {admin.role !== 'superadmin' && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant={admin.is_active ? 'outline-warning' : 'outline-success'}
                                                            onClick={() => toggleUserStatus(admin.id)}
                                                            title={admin.is_active ? 'Bloquer' : 'Débloquer'}
                                                        >
                                                            {admin.is_active ? <FaLock /> : <FaUnlock />}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={() => handleShowDeleteConfirm(admin)}
                                                            title="Supprimer"
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Modal de création */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Créer un administrateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nom d'utilisateur *</Form.Label>
                            <Form.Control
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prénom *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Téléphone *</Form.Label>
                            <Form.Control
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Mot de passe *</Form.Label>
                            <Form.Control
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                minLength={8}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Rôle *</Form.Label>
                            <Form.Select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                            >
                                <option value="admin">Admin</option>
                                <option value="superadmin">Super Admin</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="d-flex gap-2">
                            <Button type="submit" variant="primary" className="flex-fill">
                                Créer
                            </Button>
                            <Button variant="secondary" onClick={() => setShowModal(false)} className="flex-fill">
                                Annuler
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Modal Détails */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Détails de l'administrateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAdmin && (
                        <>
                            <Row>
                                <Col md={6}>
                                    <h6 className="text-muted">Informations de base</h6>
                                    <p><strong>ID :</strong> {selectedAdmin.id}</p>
                                    <p><strong>Nom d'utilisateur :</strong> {selectedAdmin.username}</p>
                                    <p><strong>Email :</strong> {selectedAdmin.email}</p>
                                    <p><strong>Nom complet :</strong> {selectedAdmin.first_name} {selectedAdmin.last_name}</p>
                                    <p><strong>Téléphone :</strong> {selectedAdmin.phone || 'Non renseigné'}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-muted">Informations système</h6>
                                    <p>
                                        <strong>Rôle :</strong>{' '}
                                        <Badge bg={selectedAdmin.role === 'superadmin' ? 'danger' : 'warning'}>
                                            {selectedAdmin.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                                        </Badge>
                                    </p>
                                    <p>
                                        <strong>Statut :</strong>{' '}
                                        <Badge bg={selectedAdmin.is_active ? 'success' : 'secondary'}>
                                            {selectedAdmin.is_active ? 'Actif' : 'Inactif'}
                                        </Badge>
                                    </p>
                                    <p>
                                        <strong>Date de création :</strong>{' '}
                                        {new Date(selectedAdmin.created_at).toLocaleDateString('fr-FR')}
                                    </p>
                                    <p>
                                        <strong>Email vérifié :</strong>{' '}
                                        <Badge bg={selectedAdmin.email_verified ? 'success' : 'warning'}>
                                            {selectedAdmin.email_verified ? 'Oui' : 'Non'}
                                        </Badge>
                                    </p>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Confirmation Suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        <strong>Attention !</strong> Cette action est irréversible.
                    </Alert>
                    {selectedAdmin && (
                        <p>
                            Êtes-vous sûr de vouloir supprimer l'administrateur{' '}
                            <strong>{selectedAdmin.first_name} {selectedAdmin.last_name}</strong> ({selectedAdmin.email}) ?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDeleteAdmin}>
                        Supprimer définitivement
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de modification */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Modifier un administrateur</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Nom d'utilisateur *</Form.Label>
                            <Form.Control
                                type="text"
                                name="username"
                                value={editFormData.username}
                                onChange={handleEditChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={editFormData.email}
                                onChange={handleEditChange}
                                required
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prénom *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="first_name"
                                        value={editFormData.first_name}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="last_name"
                                        value={editFormData.last_name}
                                        onChange={handleEditChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Téléphone *</Form.Label>
                            <Form.Control
                                type="tel"
                                name="phone"
                                value={editFormData.phone}
                                onChange={handleEditChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Rôle *</Form.Label>
                            <Form.Select
                                name="role"
                                value={editFormData.role}
                                onChange={handleEditChange}
                            >
                                <option value="admin">Admin</option>
                                <option value="superadmin">Super Admin</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Statut *</Form.Label>
                            <Form.Select
                                name="is_active"
                                value={editFormData.is_active}
                                onChange={handleEditChange}
                            >
                                <option value={true}>Actif</option>
                                <option value={false}>Inactif</option>
                            </Form.Select>
                        </Form.Group>

                        <div className="d-flex gap-2">
                            <Button type="submit" variant="primary" className="flex-fill">
                                Modifier
                            </Button>
                            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="flex-fill">
                                Annuler
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default SuperAdminManageAdmins;